import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API, isBuilding } from './model';
import { FieldError, TextField } from './Fields';
import { Icon } from './Icon';

const RANGES = [[0, 50, 'Moins de 50 m²'], [50, 100, '50 à 100 m²'], [100, 150, '100 à 150 m²'], [150, 250, '150 à 250 m²'], [250, 500, '250 à 500 m²']];

export const AddressStep = ({ data, update, errors, onBlur, track, hideMetrics = false }) => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [showRanges, setShowRanges] = useState(Boolean(data.surface_range));
  const cache = useRef(new Map());
  useEffect(() => {
    const text = (data.address || '').trim();
    setItems([]); setActive(-1); setNotice(''); setLoading(false);
    if (!focused || text.length < 3 || data.address_details?.verified) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = cache.current.get(text) || (await axios.get(`${API}/api/address/suggestions`, { params: { text }, signal: controller.signal })).data.results;
        if (controller.signal.aborted) return;
        cache.current.set(text, results); setItems(results);
        if (!results.length) setNotice('Adresse non proposée ? Vous pouvez continuer avec l’adresse saisie.');
      } catch (error) {
        if (controller.signal.aborted) return;
        setNotice('Suggestions indisponibles. Vous pouvez saisir votre adresse manuellement.');
        track('error', 'adresse', { code: 'ADDRESS_UNAVAILABLE' });
      } finally { if (!controller.signal.aborted) setLoading(false); }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [data.address, data.address_details?.verified, focused, track]);

  const choose = (item) => {
    update({ address: item.fulltext, address_details: { source: 'BAN-Geoplateforme', verified: true,
      street: item.street, postcode: item.zipcode, city: item.city, longitude: item.x, latitude: item.y } });
    setItems([]); setFocused(false);
  };
  const onKeyDown = (event) => {
    if (event.key === 'Escape') { setItems([]); setFocused(false); }
    if (!items.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); setActive((previous) => (previous + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length);
    }
    if (event.key === 'Enter' && active >= 0) { event.preventDefault(); choose(items[active]); }
  };

  return <>
    <div className="sim-field sim-address" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) { setFocused(false); setItems([]); onBlur('address'); }
    }}>
      <label htmlFor="field-address">Adresse du bien</label>
      <div className="sim-input-icon"><Icon name="pin" size={19} />
        <input id="field-address" data-testid="address-input" value={data.address || ''} maxLength={160}
          placeholder="N° et rue, code postal, ville" autoComplete="off" role="combobox" aria-autocomplete="list"
          aria-controls="address-list" aria-expanded={Boolean(items.length)} aria-invalid={Boolean(errors.address)}
          aria-describedby={errors.address ? 'error-address' : undefined} aria-activedescendant={active >= 0 && items.length ? `address-option-${active}` : undefined}
          onFocus={() => setFocused(true)} onKeyDown={onKeyDown}
          onChange={(event) => update({ address: event.target.value, address_details: { source: 'manual', verified: false } })} />
      </div>
      {items.length > 0 && <ul id="address-list" role="listbox" className="sim-address-list" data-testid="address-suggestions">
        {items.map((item, index) => <li key={`${item.fulltext}-${index}`} id={`address-option-${index}`} role="option" aria-selected={active === index} data-testid={`address-option-${index}`}>
          <button type="button" className={active === index ? 'active' : ''} data-testid={`address-select-${index}`} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(item)}><Icon name="pin" size={17} />{item.fulltext}</button>
        </li>)}
      </ul>}
      {loading && <p className="sim-field-hint" role="status" data-testid="address-loading">Recherche de votre adresse…</p>}
      {notice && <p className="sim-field-hint" role="status" data-testid="address-notice">{notice}</p>}
      {data.address_details?.verified && <p className="sim-verified" data-testid="address-verified"><Icon name="check" size={14} /> Adresse reconnue</p>}
      <FieldError name="address" errors={errors} />
    </div>
    {!hideMetrics && <>{isBuilding(data.property_type) ? <TextField name="lot_count" label="Nombre de lots" data={data} update={(patch) => update({ lot_count: patch.lot_count === '' ? '' : Number(patch.lot_count) })} errors={errors} onBlur={onBlur} type="number" min="1" step="1" inputMode="numeric" placeholder="Ex. 24" /> : <>
      <TextField name="surface" label="Surface du bien en m²" data={data} update={(patch) => update({ surface: patch.surface === '' ? '' : Number(patch.surface), surface_range: null })} errors={errors} onBlur={onBlur} type="number" min="1" step="1" inputMode="numeric" placeholder="Ex. 60" />
      <button type="button" className="sim-text-button" data-testid="surface-range-toggle" aria-expanded={showRanges} onClick={() => setShowRanges(!showRanges)}>Je ne connais pas la surface exacte <Icon name="chevron" size={15} /></button>
      {showRanges && <div className="sim-ranges" data-testid="surface-ranges" role="group" aria-label="Surface approximative">
        {RANGES.map(([min, max, label]) => <button type="button" key={min} aria-pressed={data.surface_range === label} data-testid={`surface-range-${min}-${max}`} className={data.surface_range === label ? 'is-selected' : ''} onClick={() => update({ surface: (min + max) / 2, surface_range: label })}>{label}</button>)}
      </div>}
      {data.surface_range && <p className="sim-field-hint" data-testid="surface-range-value">Surface retenue : {data.surface} m², au milieu de la tranche choisie.</p>}
    </>}</>}
  </>;
};